// @ts-nocheck
import { cloneDeep, get } from 'lodash-es';
import { CookieService } from 'ngx-cookie-service';
import { LocalStorageService, SessionStorageService } from 'ngx-webstorage';

import { Injectable } from '@angular/core';

export enum STORAGE {
  SESSION,
  LOCAL,
  COOKIE,
  MEMORY, // fallback strategy
}

export interface StorageObject {
  data: string | number;
}

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  private memory: { [key: string]: any } = {};

  private storageUnavailable: boolean;

  constructor(
    // tslint:disable-next-line: ban-types
    private cookie: CookieService,
    private localStorage: LocalStorageService,
    private sessionStorage: SessionStorageService,
  ) {
    try {
      this.set(STORAGE.LOCAL, 'storage_check', '1');
    } catch (error) {
      this.storageUnavailable = true;
    }
  }

  /**
   * retrieves value with key from storage
   * defaults to MEMORY if other storage strategies are unavailable
   * @param storage storage strategy to used
   * @param key key/ value pair to be retrieved
   */
  public get(storage: STORAGE, key: string): any {
    let value: any;
    switch (storage) {
      case STORAGE.SESSION:
        value = this.decode(this.sessionStorage.retrieve(key));
        break;
      case STORAGE.LOCAL:
        value = this.decode(this.localStorage.retrieve(key));
        break;
      case STORAGE.COOKIE:
        value = this.cookie.get(key);
        break;
      case STORAGE.MEMORY:
      default:
        value = this.decode(this.memory[key]);
        break;
    }
    // fallback memory storage if no value and key/ pair exists in memory
    if (!value && !!this.memory[key]) {
      value = this.memory[key];
    }
    // return value
    return value;
  }

  /**
   * sets value with key to storage
   * defaults to MEMORY if other storage strategies are unavailable
   * @param storage storage strategy to used
   * @param key key of value to be stored
   * @param value value to be stored
   * @param cookieOptions STORAGE.COOKIE strategy only
   */
  public set(storage: STORAGE, key: string, value: any, cookieOptions: {
    expires?: number | Date,
    path?: string,
    domain?: string,
    secure?: boolean,
    sameSite?: 'Lax' | 'Strict'
  } = {}): void {
    value = this.encode(value);
    // fail flag
    let fail = false;
    switch (storage) {
      case STORAGE.SESSION:
        value = this.sessionStorage.store(key, value);
        fail = !this.sessionStorage.retrieve(key);
        break;
      case STORAGE.LOCAL:
        value = this.localStorage.store(key, value);
        fail = !this.localStorage.retrieve(key);
        break;
      case STORAGE.COOKIE:
        value = this.cookie.set(
          key,
          value.data,
          cookieOptions.expires,
          cookieOptions.path,
          cookieOptions.domain,
          cookieOptions.secure,
          cookieOptions.sameSite,
        );
        fail = !this.cookie.check(key);
        break;
      case STORAGE.MEMORY:
      default:
        this.memory[key] = value;
        break;
    }
    // fallback memory storage on fail
    if (fail) {
      this.set(STORAGE.MEMORY, key, value);
    }
  }

  /**
   * deletes value with key from storage
   * @param storage storage strategy to used
   * @param key key/ value pair to be deleted
   * @param cookieDomain STORAGE.COOKIE strategy only
   */
  public delete(storage: STORAGE, key: string, cookieDomain?: string): void {
    let fail = false;
    switch (storage) {
      case STORAGE.SESSION:
        this.sessionStorage.clear(key);
        fail = !!this.sessionStorage.retrieve(key);
        break;
      case STORAGE.LOCAL:
        this.localStorage.clear(key);
        fail = !!this.localStorage.retrieve(key);
        break;
      case STORAGE.COOKIE:
        this.cookie.delete(key, cookieDomain);
        fail = this.cookie.check(key);
        break;
      case STORAGE.MEMORY:
      default:
        delete this.memory[key];
        break;
    }
    // fallback memory storage on fail and key/pai exists in memory
    if (fail && !!this.memory[key]) {
      this.delete(STORAGE.MEMORY, key);
    }
  }

  /**
   * encodes value
   * @param value value to be encoded
   */
  private encode(value: any): any {
    const store: StorageObject = {
      data: value,
    };
    return store;
  }

  /**
   * decodes value
   * @param value value to be decoded
   */
  private decode(value: any): any {
    const store: StorageObject = value;
    const data = get(store, 'data');
    return cloneDeep(data);
  }
}
